import { useEffect, useState, useCallback } from 'react';
import '../../styles/dashboard.css';
import calender from '../../assets/images/small/date.svg';
import api from '../../services/api';
import CustomSelect from '../CustomSelect';
import LeadGraphChart from './LeadGraphChart';

const GraphFilterComponent = () => {
  // --------------------
  // FILTER STATE
  // --------------------
  const [categoryType, setCategoryType] = useState("month"); // Default: Month
  const [loading, setLoading] = useState(false);
  const [graphData, setGraphData] = useState([]);

  // Category Filter Options
  const categoryOptions = [
    { id: "month", name: "Month" },
    { id: "year", name: "Year" },
    { id: "date", name: "Date" },
  ];

  // Month names helper
  const monthNamesShort = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  // Safely retrieve vendor ID from localStorage
  const getVendorId = () => {
    try {
      const vendorData = localStorage.getItem("vendor");
      return vendorData ? JSON.parse(vendorData)?.id : null;
    } catch (error) {
      console.error("Error reading vendor from localStorage:", error);
      return null;
    }
  };

  // --------------------
  // DATE PARSER FOR "YYYY-MM-DD HH:mm:ss"
  // --------------------
  const parseCreatedDate = (dateString) => {
    if (!dateString) return null;

    const [datePart] = dateString.trim().split(" ");
    if (!datePart) return null;

    const [yearStr, monthStr, dayStr] = datePart.split("-");
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10); // 1 - 12
    const day = parseInt(dayStr, 10);

    if (isNaN(year) || isNaN(month) || isNaN(day)) return null;

    return { year, month, day };
  };

  // --------------------
  // FETCH & PROCESS DATA BASED ON CATEGORY
  // --------------------
  const fetchAndProcessData = useCallback(async () => {
    const vendorId = getVendorId();

    if (!vendorId) {
      console.error("Vendor ID not found in localStorage");
      setGraphData([]);
      return;
    }

    setLoading(true);

    try {
      // 1. Fetch user list from vendor endpoint
      const response = await api.get(`vendor/getuserlist/${vendorId}`);
      const rawUsers = response.data?.data || [];

      const groupMap = {};

      // 2. Aggregate data based on selected categoryType (month, year, or date)
      rawUsers.forEach((user) => {
        const dateInfo = parseCreatedDate(user.created_at);
        if (!dateInfo) return;

        const { year, month, day } = dateInfo;
        let groupKey = "";
        let label = "";
        let sortValue = 0;

        if (categoryType === "year") {
          // Group by Year (e.g., "2026")
          groupKey = `${year}`;
          label = `${year}`;
          sortValue = year;
        } else if (categoryType === "date") {
          // Group by Date (e.g., "31 Jul 2026")
          const monthName = monthNamesShort[month - 1] || "";
          groupKey = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          label = `${day} ${monthName} ${year}`;
          sortValue = new Date(year, month - 1, day).getTime();
        } else {
          // Default: Group by Month (e.g., "Jul 2026")
          const monthName = monthNamesShort[month - 1] || "";
          groupKey = `${year}-${String(month).padStart(2, '0')}`;
          label = `${monthName} ${year}`;
          sortValue = new Date(year, month - 1, 1).getTime();
        }

        if (!groupMap[groupKey]) {
          groupMap[groupKey] = {
            label,
            sortValue,
            count: 0
          };
        }
        groupMap[groupKey].count += 1;
      });

      // 3. Sort chronologically and format for LeadGraphChart
      const sortedChartData = Object.values(groupMap)
        .sort((a, b) => a.sortValue - b.sortValue)
        .map((item) => ({
          name: item.label,   // X-Axis Label
          total: item.count,  // Y-Axis Value
          leads: item.count
        }));
console.log("Processed graph data:", sortedChartData);
      setGraphData(sortedChartData);
    } catch (error) {
      console.error("Error fetching vendor user list for graph:", error);
      setGraphData([]);
    } finally {
      setLoading(false);
    }
  }, [categoryType]);

  useEffect(() => {
    fetchAndProcessData();
  }, [fetchAndProcessData]);

  return (
    <div className="dashboard-content graph-filter-content">
      {/* FILTERS */}
      <div className="filter-containers">
        <form onSubmit={(e) => { e.preventDefault(); fetchAndProcessData(); }}>
          <div className="filter-form-container">
            <div className="label-container">
              <div className="image-container">
                <img src={calender} alt="category" />
              </div>
              <p>Category</p>
            </div>
            <CustomSelect
              options={categoryOptions}
              value={categoryType}
              onChange={(val) => setCategoryType(val)}
              labelKey="name"
              valueKey="id"
              placeholder="Select Category"
            />
          </div>
        </form>

        {/* GRAPH DISPLAY */}
        <div style={{ marginTop: "20px" }}>
          {loading ? (
            <p>Loading chart data...</p>
          ) : graphData.length > 0 ? (
            <LeadGraphChart data={graphData} />
          ) : (
            <p>No lead data available.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default GraphFilterComponent;